/**
 * DownloadsPage - Software downloads for purchased users
 * 
 * Requires authentication and valid purchase
 * Shows entitlement status and available downloads
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useFeatures } from '../hooks/useFeatures';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Download, Package, FileText, BookOpen, 
  ScrollText, Check, Lock, Loader2, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import './DownloadsPage.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DownloadsPage = ({ onBack }) => {
  const { getAccessToken, user } = useAuth();
  const { tier } = useFeatures();
  const [downloading, setDownloading] = useState(null);
  const [entitlement, setEntitlement] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check entitlement on mount
  useEffect(() => {
    const checkEntitlement = async () => {
      try {
        const token = getAccessToken();
        const res = await fetch(`${API}/downloads/entitlement`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setEntitlement(data);
        }
      } catch (err) {
        console.error('Failed to check entitlement:', err);
      } finally {
        setLoading(false);
      }
    };
    checkEntitlement();
  }, [getAccessToken]);

  const isPurchased = entitlement?.has_access || tier === 'STANDARD' || tier === 'PRO' || tier === 'ENTERPRISE';

  const handleDownload = async (fileType) => {
    if (!isPurchased) {
      toast.error('Purchase required to download');
      return;
    }

    setDownloading(fileType);
    
    try {
      const token = getAccessToken();
      const res = await fetch(`${API}/downloads/${fileType}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Download failed');
      }

      // Get filename from Content-Disposition header
      const disposition = res.headers.get('Content-Disposition');
      let filename = `riskee-${fileType}.zip`;
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }

      // Download the file
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      console.error('Download error:', err);
      toast.error(err.message || 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="downloads-loading">
        <Loader2 className="animate-spin" size={32} />
        <p>Loading downloads...</p>
      </div>
    );
  }

  return (
    <div className="page downloads-page" data-testid="downloads-page">
      <div className="page-header">
        <div>
          <h1>Downloads</h1>
          <p>RISKEE software and documentation</p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft size={16} />
            Back to Dashboard
          </Button>
        )}
      </div>

      {/* Entitlement Status */}
      <Card className={`entitlement-card ${isPurchased ? 'purchased' : 'not-purchased'}`}>
        <div className="entitlement-status">
          {isPurchased ? (
            <>
              <Check size={24} className="entitlement-icon purchased" />
              <div className="entitlement-info">
                <h3>RISKEE Purchased</h3>
                <p>Version Access: v1.x</p>
                <p className="entitlement-detail">Downloads Available</p>
              </div>
            </>
          ) : (
            <>
              <Lock size={24} className="entitlement-icon locked" />
              <div className="entitlement-info">
                <h3>Purchase Required</h3>
                <p>Complete your purchase to access downloads</p>
                <Button 
                  variant="default" 
                  size="sm" 
                  className="purchase-btn-inline"
                  onClick={() => window.dispatchEvent(new CustomEvent('navigate-billing'))}
                >
                  Purchase RISKEE — $99 AUD
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Downloads Section */}
      <div className="downloads-section">
        <div className="downloads-header">
          <h2>RISKEE v1.0</h2>
          <span className="release-date">Released: July 2026</span>
        </div>

        <div className="downloads-grid">
          {/* Self-Hosted Package */}
          <Card className="download-card">
            <div className="download-card-icon">
              <Package size={28} />
            </div>
            <div className="download-card-content">
              <h4>Self-Hosted Package</h4>
              <p>Complete Docker deployment with MongoDB, API, and Caddy reverse proxy</p>
              <span className="download-meta">ZIP • ~2 MB</span>
            </div>
            <Button
              variant={isPurchased ? "default" : "outline"}
              disabled={!isPurchased || downloading === 'package'}
              onClick={() => handleDownload('package')}
              data-testid="download-package-btn"
            >
              {downloading === 'package' ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <Download size={16} />
              )}
              {isPurchased ? 'Download' : 'Locked'}
            </Button>
          </Card>

          {/* Deployment Guide */}
          <Card className="download-card">
            <div className="download-card-icon">
              <FileText size={28} />
            </div>
            <div className="download-card-content">
              <h4>Deployment Guide</h4>
              <p>Step-by-step instructions for server deployment and configuration</p>
              <span className="download-meta">PDF • ~500 KB</span>
            </div>
            <Button
              variant={isPurchased ? "default" : "outline"}
              disabled={!isPurchased || downloading === 'deployment-guide'}
              onClick={() => handleDownload('deployment-guide')}
              data-testid="download-deployment-btn"
            >
              {downloading === 'deployment-guide' ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <Download size={16} />
              )}
              {isPurchased ? 'Download' : 'Locked'}
            </Button>
          </Card>

          {/* Installation Guide */}
          <Card className="download-card">
            <div className="download-card-icon">
              <BookOpen size={28} />
            </div>
            <div className="download-card-content">
              <h4>Installation Guide</h4>
              <p>Quick start guide for getting RISKEE running on your infrastructure</p>
              <span className="download-meta">PDF • ~300 KB</span>
            </div>
            <Button
              variant={isPurchased ? "default" : "outline"}
              disabled={!isPurchased || downloading === 'installation-guide'}
              onClick={() => handleDownload('installation-guide')}
              data-testid="download-installation-btn"
            >
              {downloading === 'installation-guide' ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <Download size={16} />
              )}
              {isPurchased ? 'Download' : 'Locked'}
            </Button>
          </Card>

          {/* Release Notes */}
          <Card className="download-card">
            <div className="download-card-icon">
              <ScrollText size={28} />
            </div>
            <div className="download-card-content">
              <h4>Release Notes</h4>
              <p>Version history, changelog, and known issues</p>
              <span className="download-meta">PDF • ~100 KB</span>
            </div>
            <Button
              variant={isPurchased ? "default" : "outline"}
              disabled={!isPurchased || downloading === 'release-notes'}
              onClick={() => handleDownload('release-notes')}
              data-testid="download-notes-btn"
            >
              {downloading === 'release-notes' ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <Download size={16} />
              )}
              {isPurchased ? 'Download' : 'Locked'}
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DownloadsPage;
